// Store DC collection requests and their scan/collect lifecycle.
import {
  StoreDcCreateRequest,
  StoreDcModHintsQuery,
  StoreDcModHintsResponse,
  StoreDcModTagHintsQuery,
  StoreDcModTagHintsResponse,
  StoreDcRequest,
  StoreDcRequestsResponse,
  StoreDcRequestStatus,
  StoreDcScanRequest,
} from '../../types/storeDc';
import { forgeRequest } from '../forgeRequest';
import { FORGE_STORE_DC_API_URL } from './endpoints';

export async function createStoreDcRequest(
  token: string,
  request: StoreDcCreateRequest
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests`,
    'Error creating Store DC request',
    { method: 'POST', body: request }
  );
}

export async function fetchStoreDcRequests(
  token: string,
  filters: { chainId: number; status: StoreDcRequestStatus }
): Promise<StoreDcRequestsResponse> {
  const params = new URLSearchParams({
    chain_id: String(filters.chainId),
    status: filters.status,
  });
  return forgeRequest<StoreDcRequestsResponse>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests?${params.toString()}`,
    'Error fetching Store DC requests'
  );
}

export async function fetchStoreDcRequest(
  token: string,
  requestId: string
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests/${encodeURIComponent(requestId)}`,
    'Error fetching Store DC request'
  );
}

export async function scanStoreDcRequestItem(
  token: string,
  requestId: string,
  itemIndex: number,
  request: StoreDcScanRequest
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests/${encodeURIComponent(requestId)}/items/${itemIndex}/scan`,
    'Error scanning Store DC item',
    { method: 'POST', body: request }
  );
}

export async function batchScanStoreDcRequestItems(
  token: string,
  requestId: string,
  itemIndexes: number[],
  request: StoreDcScanRequest
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests/${encodeURIComponent(requestId)}/items/scan`,
    'Error scanning Store DC items',
    { method: 'POST', body: { ...request, itemIndexes } }
  );
}

export async function markStoreDcRequestItemCollectedElsewhere(
  token: string,
  requestId: string,
  itemIndex: number
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests/${encodeURIComponent(requestId)}/items/${itemIndex}/collected-elsewhere`,
    'Error marking Store DC item as collected elsewhere',
    { method: 'POST' }
  );
}

export async function collectStoreDcRequest(
  token: string,
  requestId: string
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests/${encodeURIComponent(requestId)}/collected`,
    'Error collecting Store DC request',
    { method: 'POST' }
  );
}

export async function cancelStoreDcRequest(
  token: string,
  requestId: string
): Promise<StoreDcRequest> {
  return forgeRequest<StoreDcRequest>(
    token,
    `${FORGE_STORE_DC_API_URL}/requests/${encodeURIComponent(requestId)}/cancel`,
    'Error cancelling Store DC request',
    { method: 'POST' }
  );
}

export async function fetchStoreDcModHints(
  token: string,
  query: StoreDcModHintsQuery
): Promise<StoreDcModHintsResponse> {
  const params = new URLSearchParams({ store_id: String(query.storeId) });
  if (query.itemNumber !== undefined) {
    const itemNumber = query.itemNumber.trim();
    if (!itemNumber) throw new Error('Store DC mod hints require a non-empty target identity');
    params.set('item_number', itemNumber);
  } else {
    const normalizedBarcode = query.normalizedBarcode.trim();
    if (!normalizedBarcode) {
      throw new Error('Store DC mod hints require a non-empty target identity');
    }
    params.set('normalized_barcode', normalizedBarcode);
  }
  return forgeRequest<StoreDcModHintsResponse>(
    token,
    `${FORGE_STORE_DC_API_URL}/mod-hints?${params.toString()}`,
    'Error fetching Store DC mod hints'
  );
}

export async function fetchStoreDcModTagHints(
  token: string,
  query: StoreDcModTagHintsQuery
): Promise<StoreDcModTagHintsResponse> {
  const modTags = [...new Set(query.modTags.map((modTag) => modTag.trim()).filter(Boolean))];
  if (modTags.length === 0) {
    throw new Error('Store DC mod-tag hints require at least one mod tag');
  }
  if (modTags.length > 50) {
    throw new Error('Store DC mod-tag hints accept at most 50 mod tags');
  }
  const params = new URLSearchParams({ store_id: String(query.storeId) });
  modTags.forEach((modTag) => params.append('mod_tags', modTag));
  return forgeRequest<StoreDcModTagHintsResponse>(
    token,
    `${FORGE_STORE_DC_API_URL}/mod-tag-hints?${params.toString()}`,
    'Error fetching Store DC mod-tag hints'
  );
}
